import PropTypes from 'prop-types';
import React, { useMemo, useRef } from 'react';

import { Color, FontSize, FontWeight } from '../../../typings/design-tokens';
import { IComponent } from '../../../typings/generic-component';
// import { ViewportList } from 'react-viewport-list'; -> see https://github.com/oleggrishechkin/react-viewport-list
import { IClassNames, makeClassNames } from '../../../utils/styles';
import Text from '../../atoms/text/text';
import { ISearchOption } from '../search-input/search-input';

import './municipality-list.scss';

export interface IMunicipalityList extends IComponent {
  municipalityList: IMunicipality[];
  activeLetter?: string;
}

export interface IMunicipality extends ISearchOption {
  ballot?: boolean;
}

// Style
const LINK_COLOR: Color = 'dark';
const LINK_FONT_SIZE: FontSize = '14';
const LINK_FONT_WEIGHT: FontWeight = '400';

const classNames = (): IClassNames =>
  makeClassNames({
    municipalityList: 'municipality-list',
    municipality: 'municipality-list__item',
    hasBallot: 'municipality-list--has-ballot',
    dot: 'municipality-list__dot',
    listContainer: 'list-container',
    isHidden: 'municipality-list--hidden',
  });

interface IListElement {
  municipality: IMunicipality;
  index: number;
  cn: IClassNames;
}

const ListElement: React.FC<IListElement> = ({ municipality, index, cn }) => (
  <li className={`${cn.municipality} ${municipality.ballot ? cn.hasBallot : ''}`} key={index}>
    <Text tag="a" href={municipality.url} color={LINK_COLOR} fontSize={LINK_FONT_SIZE} fontWeight={LINK_FONT_WEIGHT}>
      {municipality.name}
    </Text>
  </li>
);
const MemoizedListElement = React.memo(ListElement);

// Helper for memoized component
type ReducedMunicipalityList = {
  [key: string]: IMunicipality[];
};

interface IMunicipalityListBlock {
  municipalityListObject: ReducedMunicipalityList;
  isHidden: boolean;
  startingLetter: string;
}

const MunicipalityListByLetterBlock: React.FC<IMunicipalityListBlock> = ({
  municipalityListObject,
  isHidden,
  startingLetter,
}) => {
  const cn = classNames();
  const ref = useRef<HTMLUListElement | null>(null);

  // return (
  //   <ul className={`${cn.municipalityList} ${isHidden && cn.isHidden}`} ref={ref}>
  //     <ViewportList
  //       viewportRef={ref}
  //       itemMinSize={50}
  //       // initialAlignToTop={false}
  //       items={municipalityListObject[startingLetter].sort((prev, next) => prev.name.localeCompare(next.name))}
  //     >
  //       {(municipality: IMunicipality, index: number) => (
  //         <MemoizedListElement municipality={municipality} key={index} index={index} cn={cn} />
  //       )}
  //     </ViewportList>
  //   </ul>
  // );

  return (
    <ul className={`${cn.municipalityList} ${isHidden && cn.isHidden}`} ref={ref}>
      {municipalityListObject[startingLetter]
        .sort((prev, next) => prev.name.localeCompare(next.name))
        .map((municipality: IMunicipality, index: number) => (
          <MemoizedListElement municipality={municipality} key={index} index={index} cn={cn} />
        ))}
    </ul>
  );
};
// const MemoizedMunicipalityListBlock = React.memo(MunicipalityListByLetterBlock); // FIXME: not useful, data changes each row

const MunicipalityList: React.FC<IMunicipalityList> = ({ municipalityList, activeLetter = 'A' }) => {
  const cn = classNames();

  const municipalityListByLetter: ReducedMunicipalityList = useMemo(
    () =>
      municipalityList.reduce((acc: { [key: string]: any }, municipality) => {
        return { ...acc, [municipality.name[0]]: [...(acc[municipality.name[0]] || []), municipality] };
      }, {}),
    [municipalityList]
  );

  const isMunicipalityListHidden = (startingLetter: string) => {
    return activeLetter !== startingLetter ? cn.isHidden : '';
  };

  return (
    <div id="list-container" className={cn.listContainer}>
      {Object.keys(municipalityListByLetter)
        // .sort((prev, next) => prev.localeCompare(next)) // Sorts BLOCKS of CITIES inside PAGE
        .map((startingLetter) => (
          <MunicipalityListByLetterBlock
            key={startingLetter}
            startingLetter={startingLetter}
            municipalityListObject={municipalityListByLetter}
            isHidden={isMunicipalityListHidden(startingLetter)}
          />
        ))}
    </div>
  );
};

MunicipalityList.propTypes = {
  activeLetter: PropTypes.string,
  municipalityList: PropTypes.arrayOf<IMunicipality>(
    PropTypes.shape({
      ballot: PropTypes.bool.isRequired,
      id: PropTypes.string.isRequired,
      name: PropTypes.string.isRequired,
      url: PropTypes.string.isRequired,
    }).isRequired
  ).isRequired,
};

export default MunicipalityList;
